/**
 * Transport-level retry policy for the shared axios client.
 *
 * Context — the failure this exists for: the background sync cycle
 * (`sync/refresh`, see infra/sync) fans out five requests at once —
 * GET /todos, /notifications, /calendar/events, /household and
 * POST /rules/sync/snapshot. When the browser tab is frozen or the host
 * suspends, every one of those sockets dies at the same instant and each
 * request aborts with `ECONNABORTED` (`reached_server: false`,
 * `navigator.onLine: true`) — 10s later if the tab is merely throttled,
 * up to 30 minutes later if it was frozen outright and the XHR timeout
 * only got dispatched on unfreeze. With no retry, a whole cycle's worth
 * of data was simply dropped until the next reconnect.
 *
 * What this module decides:
 *
 *  1. WHICH failures are retryable — transport failures only (nothing
 *     reached the server). HTTP status errors keep the existing
 *     semantics: they surface to the caller unchanged, so per-feature
 *     policies (e.g. notifications' 429/503 backoff) stay authoritative
 *     and we never double-retry a 500.
 *
 *  2. WHICH requests may be retried — safe methods (GET/HEAD/OPTIONS)
 *     always, anything else only when the call site opts in via
 *     `retryOnTransportError: true`. A timeout is NOT proof the server
 *     never processed the request (the response could have been lost on
 *     the way back), so blindly replaying a POST/PATCH/DELETE risks a
 *     duplicate write. Read-shaped POSTs — POST /rules/sync/snapshot is
 *     the only one today — opt in explicitly.
 *
 *  3. HOW LONG to wait — AWS-style *full* jitter:
 *     `random(0, min(cap, base * 2^(n-1)))`. Full jitter (rather than
 *     axios-retry's built-in ±20% `exponentialDelay`) is what keeps the
 *     five concurrent requests from re-firing as a synchronized burst:
 *     they fail within 2ms of each other, so a tight jitter band would
 *     just move the stampede rather than break it up.
 */

import type { AxiosError, AxiosRequestConfig } from 'axios';
import { classifyHttpError } from './http-error';

// Per-request opt-in for non-idempotent methods. Declared on axios' own
// config interface so call sites get it typed without a cast.
declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Allow this request to be replayed after a transport-level failure
     * even though its method isn't inherently safe. Only set this on
     * requests that are idempotent in effect (a read-shaped POST, or a
     * write carrying a client-authored id the server dedupes on).
     */
    retryOnTransportError?: boolean;
  }
}

/** Attempts after the first. 2 retries → at most 3 attempts per request. */
export const RETRY_MAX_RETRIES = 2;
/** First backoff window; doubles per retry. */
export const RETRY_BASE_DELAY_MS = 500;
/** Ceiling on the backoff window, before jitter. */
export const RETRY_MAX_DELAY_MS = 8000;

const SAFE_METHODS = new Set(['get', 'head', 'options']);

/**
 * True when the request died before the server answered — the only class
 * of failure this policy retries. `canceled` is excluded on purpose: an
 * abort is a deliberate act (a superseded fetch, or the resume detector
 * tearing down requests stranded on dead sockets), and replaying it would
 * undo the cancellation.
 */
export function isTransportFailure(error?: AxiosError): boolean {
  const info = classifyHttpError(error);
  if (info.reachedServer) return false;
  return info.kind === 'timeout' || info.kind === 'network';
}

/** True when this request's method (or its explicit opt-in) allows a replay. */
export function isReplayableRequest(config?: AxiosRequestConfig): boolean {
  if (!config) return false;
  if (config.retryOnTransportError === true) return true;
  return SAFE_METHODS.has((config.method || 'get').toLowerCase());
}

/** axios-retry `retryCondition`: retry transport failures on replayable requests. */
export function shouldRetryTransportError(error: AxiosError): boolean {
  return isTransportFailure(error) && isReplayableRequest(error.config);
}

/**
 * Full jitter: a uniform draw from [0, min(cap, base * 2^(n-1))].
 * `retryNumber` is 1-based (axios-retry increments before calling).
 * `random` is injectable so tests can assert the window rather than a
 * flaky sampled value.
 */
export function fullJitterDelayMs(
  retryNumber: number,
  random: () => number = Math.random,
  base: number = RETRY_BASE_DELAY_MS,
  cap: number = RETRY_MAX_DELAY_MS,
): number {
  const window = Math.min(cap, base * Math.pow(2, Math.max(0, retryNumber - 1)));
  return Math.round(random() * window);
}
